import ComputeNode from '@/common/models/computeNode.model';
import waitTask from '@/common/utils/waitTask';
import { TASK_STATE_IS_IN_PROGRESS } from '@/common/constants/tasks.constants';
import { TaskState } from '@schema';
import { TaskName } from '@/common/constants/taskNames.constants';
import useTasksStore from '@/common/store/pinia/tasks';

const COMPUTE_NODE_ROLES_COMPUTE = 'compute';

export default {
  name: 'computeNodes',
  resource: '/api/v2/compute/nodes',
  Model: ComputeNode,
  socketUpdates: true,
  getters: {
    getHypervisorType: (_, getters) => node => getters.getItemByProp('name')(node)?.hypervisorType
  },
  actions: ({ resource, Model }) => ({
    SET_NODES_RELEASE({ commit }, nodesArr) {
      nodesArr.forEach(id => commit('CHANGE',
        { item: new Model({ id, state: 'releasing' }) }
      ));
    },
    ADD({ commit, rootGetters }, ids) {
      const nodesArr = typeof ids === 'string' ? [ids] : ids;
      const nodes = nodesArr.map(id => ({ node_id: id, roles: [COMPUTE_NODE_ROLES_COMPUTE] }));
      const event = {
        nodeNames: nodesArr.map(id => rootGetters['locations/getNodeById'](id).name)
      };

      return this.$api.put(resource, { nodes, event })
        .then((res) => {
          commit('CLEAR_ERRORS');
          return res;
        })
        .catch((error) => {
          commit('SET_ERRORS', error);

          throw error;
        });
    },
    RELEASE({ commit, dispatch }, nodes) {
      const nodeIds = nodes.map(node => node.id);
      const nodeNames = nodes.map(node => node.name);
      const body = {
        nodes: nodeIds.map(id => ({ node_id: id, roles: [COMPUTE_NODE_ROLES_COMPUTE] })),
        event: { nodeNames }
      };

      return this.$api.delete(resource, body)
        .then((res) => {
          dispatch('SET_NODES_RELEASE', nodeIds);
          commit('CLEAR_ERRORS');
          return res;
        })
        .then(waitTask)
        .then((res) => {
          commit('CLEAR_ERRORS');
          nodeIds.forEach(nodeId => commit('REMOVE', nodeId));
          return res;
        })
        .catch((error) => {
          commit('SET_ERRORS', error);

          throw error;
        });
    },
    FETCH_ITEMS_WITH_PROGRESS_NODES({ dispatch }, params) {
      return dispatch('FETCH_ITEMS', params).then(() => dispatch('SHOW_NODES_IN_PROGRESS'));
    },
    SHOW_NODES_IN_PROGRESS({ dispatch }) {
      const tasksStore = useTasksStore();
      const tasks = tasksStore.getRunningTasks([TaskName.COMPUTE_JOINING_NODE, TaskName.COMPUTE_RELEASE_NODE]);

      dispatch('JOIN_NODES', tasks);
    },
    JOIN_NODES({ commit }, tasks) {
      tasks.forEach((task) => {
        const nodes = task.payload?.nodes || task.event?.nodes || [];

        nodes.forEach((nodeItem) => {
          const nodeId = nodeItem.node_id || nodeItem;
          const node = this.getters['nodes/getItemById'](nodeId) || {};
          const item = new Model({
            id: nodeId,
            host_ip: node.ip,
            orig_hostname: node.host,
            host: node.attributes?.host,
            state: task.name === TaskName.COMPUTE_JOINING_NODE ? 'joining' : 'releasing'
          });

          commit('CHANGE', { item });
        });
      });
    },

    socket_compute({ getters, dispatch }, { object, operation }) {
      if (operation === 'updated' && object.changed_node_states) {
        const ids = Object.keys(object.changed_node_states);
        ids.forEach((id) => {
          if (getters.getItemById(id)) {
            dispatch('TRIGGER_CHANGE', { id, state: object.changed_node_states[id] });
          }
        });
      }
    },
    CHANGE({ getters, commit }, data) {
      const computeNode = getters.getItemById(data.node_id);
      if (computeNode) {
        commit('CHANGE', { item: new Model(data) });
      }
    },

    TRIGGER_CHANGE({ commit }, data) {
      commit('CHANGE', { item: new Model(data) });
    },

    socket_tasks({ dispatch }, task) {
      const { state, event } = task;
      const { nodes } = event || {};

      if (event?.name === TaskName.COMPUTE_JOINING_NODE) {
        if (TASK_STATE_IS_IN_PROGRESS.includes(state)) {
          return dispatch('JOIN_NODES', [task]);
        }
        if ([TaskState.SUCCESS, TaskState.FAILED].includes(state)) {
          return dispatch('FETCH_ITEMS_WITH_PROGRESS_NODES');
        }
      }

      if (event?.name === TaskName.COMPUTE_RELEASE_NODE) {
        if (TASK_STATE_IS_IN_PROGRESS.includes(state)) {
          return dispatch('SET_NODES_RELEASE', nodes);
        }
        if (state === TaskState.SUCCESS) {
          return dispatch('FETCH_ITEMS_WITH_PROGRESS_NODES');
        }
        if (state === TaskState.FAILED) {
          return Promise.all(nodes.map(id => dispatch('CHANGE', { id, state: 'up' })));
        }
      }

      if (task?.name === TaskName.COMPUTE_RECONFIGURE && state === TaskState.SUCCESS) {
        return dispatch('FETCH_ITEMS_WITH_PROGRESS_NODES', { with_stats: true, options: { merge: false } });
      }

      return Promise.resolve();
    }
  })
};
